import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Hub } from 'aws-amplify/utils';

import { SIGNIN_PATH } from './auth.models';
import { AuthService } from './auth.service';
import { AuthStore } from './auth.store';
import { AiUsageStore } from '../ai-usage/ai-usage.store';
import { MeStore } from '../me/me.store';
import { TenantsStore } from '../tenants/tenants.store';

/**
 * Amplify Auth Hub のグローバル購読。
 * - signedOut: 保護ページにいる場合は signin に戻す
 * - tokenRefresh_failure: 無効化された refresh token が localStorage に残らないよう
 *   Amplify 側で signOut してから signin へ戻す
 *
 * signedIn 等のページ固有遷移は SigninPage の個別 listener に委ねる。
 * provideAppInitializer から 1 度だけ呼び出すこと（リスナーはアプリ寿命と一致）。
 */
export const setupGlobalAuthListener = (): void => {
  const authStore = inject(AuthStore);
  const authService = inject(AuthService);
  const meStore = inject(MeStore);
  const tenantsStore = inject(TenantsStore);
  const aiUsageStore = inject(AiUsageStore);
  const router = inject(Router);

  const syncAndRedirect = async (): Promise<void> => {
    await authStore.checkAuthStatus();
    if (!authStore.isAuthenticated() && !router.url.startsWith(SIGNIN_PATH)) {
      await router.navigate([SIGNIN_PATH]);
    }
  };

  Hub.listen('auth', ({ payload }) => {
    switch (payload.event) {
      case 'signedOut': {
        // 前ユーザーの MyInfo / テナント情報 / AI 利用量が次セッションに残らないよう
        // signin に戻す前にリセットする。
        // AiUsageStore はテナント単位のキャッシュ判定を持つため、リセットしないと
        // 同一テナントに再サインインしたときに前セッションのスナップショットを
        // そのまま表示し続けてしまう。
        meStore.reset();
        tenantsStore.reset();
        aiUsageStore.reset();
        void syncAndRedirect();
        break;
      }
      case 'tokenRefresh_failure': {
        // 無効化された refresh token が localStorage に残るのを防ぐため Amplify 側で signOut する。
        // 成功時は signedOut イベント経由で syncAndRedirect が走るため、ここでは呼ばない。
        // signOut() 自体が失敗した場合のフォールバックとしてのみ syncAndRedirect を実行する。
        void (async () => {
          try {
            await authService.signOut();
          } catch (err) {
            // oxlint-disable-next-line no-console -- signOut 失敗の原因解析用 (viewer にロガーが無いため console を使う)
            console.error(
              '[setupGlobalAuthListener] signOut on tokenRefresh_failure failed',
              err,
            );
            await syncAndRedirect();
          }
        })();
        break;
      }
      default:
        break;
    }
  });
};
